import type {
  FactoryOptionDto,
  MachineAttachmentDto,
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import {
  type EqOptionType,
  GENERAL_MACHINE_TYPES,
  handleIdGeneration,
  Options,
  OTHER,
  setIsPristine
} from '@common/utils'
import {
  MolsonBox,
  MolsonCustomTooltip,
  MolsonIcon,
  MolsonToggle,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { type ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { CollapseItems } from '../collapse-items/CollapseItems'
import { FACTORY_OPTIONS_TYPES } from '../constants'
import { CustomFit } from '../custom-fit'
import { FactoryFitsOptions } from '../factory-fit-options/FactoryFitOptions'
import type {
  AddCustomFactoryFitFunctionProps,
  FactoryOptionDtoWithChecked,
  HandleOptionChangeFunctionProps
} from '../types'

export type FactoryFitsProps = {
  archivedFactoryFits: FactoryOptionDtoWithChecked[]
  displayCustomOptions: boolean
  expandedFamilies?: Set<string>
  factoryFitsChecked: boolean
  addCustomFactoryFit?: (options: AddCustomFactoryFitFunctionProps) => void
  toggleFactoryFitChecked?: () => void
  setExpandedFamilies?: (value: string) => void
  setFactoryFitsNames?: (values: string[]) => void
  closeExpandedFamilyOrGroup?: (
    eqOptionType: EqOptionType,
    familyOrGroup: string
  ) => void
  handleOptionChange: HandleOptionChangeFunctionProps
  machine?: MachineAttachmentDto | MachineDto
  quotationItem?: MachineAttachmentItemDto | MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  translationsPrefix: string
}

export const FactoryFits = ({
  addCustomFactoryFit,
  archivedFactoryFits,
  displayCustomOptions,
  expandedFamilies,
  setExpandedFamilies,
  setFactoryFitsNames,
  closeExpandedFamilyOrGroup,
  factoryFitsChecked,
  handleOptionChange,
  machine,
  quotationItem,
  quotationItemOldPrices,
  toggleFactoryFitChecked,
  translationsPrefix
}: FactoryFitsProps) => {
  const theme = molsonUseTheme()
  const { t } = useTranslation('', {
    keyPrefix: translationsPrefix
  })

  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParam,
    specId: specIdParam
  } = useParams()

  const quoteId = quoteIdParam ?? specIdParam

  const [customFactoryFitExpanded, setCustomFactoryFitExpanded] =
    useState(false)

  const isMachine = machine?.machineType === GENERAL_MACHINE_TYPES.MACHINE

  const orderFamiliesAndGroups = useCallback(
    (familiesAndGroups: string[]): string[] => {
      const otherIndex = familiesAndGroups.indexOf(OTHER)

      if (otherIndex === -1 || otherIndex === familiesAndGroups.length - 1) {
        return familiesAndGroups
      }

      familiesAndGroups.splice(otherIndex, 1)
      familiesAndGroups.push(OTHER)
      return familiesAndGroups
    },
    []
  )

  const factoryFits: FactoryOptionDto[] | undefined = isMachine
    ? machine?.factoryOptions?.filter(
        (factoryOption: FactoryOptionDto) =>
          factoryOption.type !== FACTORY_OPTIONS_TYPES.STANDARD
      )
    : machine?.factoryOptions?.filter(
        (factoryOption: FactoryOptionDto) =>
          factoryOption.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
      )

  const familiesList = () => {
    return Array.from(
      new Set(
        factoryFits?.map(element => {
          const key = 'family'
          if (element[key] && element[key] !== '') {
            return element.family
          }
        })
      )
    ).filter(element => element !== undefined && element !== null)
  }
  const groupsList = () => {
    return Array.from(
      new Set(
        factoryFits?.map(element => {
          const key = 'group'
          if (element[key] && element[key] !== '') {
            return element.group
          }
        })
      )
    ).filter(element => element !== undefined && element !== null)
  }

  const uniqueFamiliesAndGroups = useMemo(() => {
    const list = [
      ...new Set(
        factoryFits?.map((obj: FactoryOptionDto) => {
          if (obj.family && obj.family !== '') {
            return obj.family
          }
        })
      ),
      ...new Set(
        factoryFits?.map((obj: FactoryOptionDto) => {
          if (obj.group && obj.group !== '') {
            return obj.group
          }
        })
      )
    ]
    return list.filter(element => element)
  }, [factoryFits])

  const orderedUniqueFamiliesAndGroups = useMemo(() => {
    const factoryFamiliesAndGroups = orderFamiliesAndGroups(
      uniqueFamiliesAndGroups as string[]
    )

    const isAllArchived = (options: typeof factoryFits) => {
      if (options && options?.length > 0) {
        return options.every(option => option?.isArchived)
      }
    }

    const familiesAndGroupsToHide = factoryFamiliesAndGroups.filter(element => {
      const familyOptions =
        factoryFits?.filter(option => option.family === element) ?? []
      const groupOptions =
        factoryFits?.filter(option => option.group === element) ?? []

      return isAllArchived(familyOptions) || isAllArchived(groupOptions)
    })

    const archivedFamilies = new Set(archivedFactoryFits?.map(e => e.family))
    const archivedGroups = new Set(archivedFactoryFits?.map(e => e.group))

    const filteredToHide = familiesAndGroupsToHide.filter(
      item => !(archivedFamilies.has(item) || archivedGroups.has(item))
    )

    return factoryFamiliesAndGroups.filter(
      item => !filteredToHide.includes(item)
    )
  }, [
    uniqueFamiliesAndGroups,
    factoryFits,
    archivedFactoryFits,
    orderFamiliesAndGroups
  ])

  const handleAddFactoryOption = (
    event: ChangeEvent<HTMLInputElement>,
    factoryOption: FactoryOptionDto
  ) => {
    if (
      machine &&
      quotationItem &&
      factoryOption.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
    ) {
      if (equipmentId && attachmentId) {
        const option = event.target.checked
          ? Options.ADD_FACTORY_FIT_WITHIN_MACHINE
          : Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE

        handleOptionChange({
          machineId: equipmentId,
          option: option,
          optionId: factoryOption.id as number,
          quotationId: quoteId as string
        })

        return
      }

      const option = event.target.checked
        ? Options.ADD_FACTORY_FIT
        : Options.REMOVE_FACTORY_FIT

      handleOptionChange({
        option: option,
        optionId: factoryOption.id as number,
        quotationId: quoteId as string
      })
    }
  }

  const selectedFactoryOptions = quotationItem?.factoryOptionItems ?? []

  const customFactoryOptions = selectedFactoryOptions?.filter(
    item => (item?.id ?? 0) < 0
  )

  const onDeleteFactoryFit = (id: number) => {
    const isWithinMachine = Boolean(attachmentId && equipmentId)

    if (isWithinMachine) {
      handleOptionChange({
        machineId: equipmentId,
        option: Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE,
        optionId: id,
        quotationId: quoteId as string
      })
    } else {
      handleOptionChange({
        option: Options.REMOVE_FACTORY_FIT,
        optionId: id,
        quotationId: quoteId as string
      })
    }

    setIsPristine(false)
  }

  const options =
    customFactoryOptions.length > 0
      ? customFactoryOptions.map(item => ({
          dnp: item.dnp ?? 0,
          id: item.id,
          name: item.name,
          isNewValue: false
        }))
      : [
          {
            dnp: 0,
            id: Number(handleIdGeneration()),
            name: '',
            isNewValue: true
          }
        ]

  const toggleLabel = factoryFitsChecked
    ? t('hideFactoryfitOptions')
    : t('showFactoryfitOptions')

  const hasArchivedFactoryOptions = quotationItem?.factoryOptionItems?.some(
    item => item.isArchived
  )

  return (
    <MolsonBox
      sx={{
        background: theme.palette.secondary[50],
        borderRadius: '8px',
        px: 3,
        py: 5
      }}
      boxShadowType="default"
    >
      <MolsonBox
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        mb={factoryFitsChecked ? 4 : 0}
      >
        <MolsonBox alignItems="center" display="flex" gap={0.5}>
          {hasArchivedFactoryOptions && (
            <MolsonIcon variant="ERROR_OUTLINE" iconColor="error" />
          )}
          <MolsonTypography variant="h3">{t('factoryFit')}</MolsonTypography>
          <MolsonCustomTooltip
            config={{
              description: t('factoryFitDescription'),
              title: t('factoryFit')
            }}
          />
        </MolsonBox>
        <MolsonToggle
          onChange={() => {
            toggleFactoryFitChecked?.()
          }}
          checked={factoryFitsChecked}
          label={toggleLabel}
        />
      </MolsonBox>

      <CollapseItems checked={factoryFitsChecked}>
        {orderedUniqueFamiliesAndGroups.map((element: string) => {
          const isFamily = familiesList().includes(element)
          const isGroup = groupsList().includes(element)

          return (
            //@ts-ignore
            <FactoryFitsOptions
              archivedFactoryFits={archivedFactoryFits}
              expandedFamilies={expandedFamilies}
              setExpandedFamilies={setExpandedFamilies}
              setFactoryFitsNames={setFactoryFitsNames}
              closeExpandedFamilyOrGroup={closeExpandedFamilyOrGroup}
              factoryFits={factoryFits as FactoryOptionDto[]}
              setTypeName={element}
              isFamily={isFamily}
              isGroup={isGroup}
              handleAddFactoryOption={handleAddFactoryOption}
              handleOptionChange={handleOptionChange}
              key={element}
              machine={machine}
              quotationItem={quotationItem}
              quotationItemOldPrices={quotationItemOldPrices}
            />
          )
        })}

        {displayCustomOptions && (
          <CustomFit
            expanded={customFactoryFitExpanded}
            key={selectedFactoryOptions.length}
            onDelete={onDeleteFactoryFit}
            onSave={addCustomFactoryFit}
            options={options}
            setExpanded={setCustomFactoryFitExpanded}
          />
        )}
      </CollapseItems>
    </MolsonBox>
  )
}
